import React, { useState, useEffect } from "react";
import {
  Drawer,
  Spin,
  Form,
  Input,
  Select,
  Button,
  Upload,
  Divider,
  InputNumber,
  message,
  Checkbox,
  Space,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form";
import {
  useActiveStoresQuery,
  useActiveUnitsQuery,
  useBrandsQuery,
  useCategoriesQuery,
  usePrintersQuery,
  useSuppliersQuery,
} from "../../../Services";
import { Product } from "../../../Types";
import { BrandModal, SupplierModal, CategoryModal } from "../../../components";
import { StockForm } from "./StockForm";
import { IMAGE_URL } from "../../../Constant";
import { templateData } from "./downloadTemplate";
import {
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

const { Option } = Select;

interface ProductFormProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onSubmit: () => void;
  editingProduct: Product | null;
  form: FormInstance;
  excelSheetUploading: boolean;
  triggerImportClick: () => void;
}

export const ProductForm: React.FC<ProductFormProps> = ({
  open,
  onClose,
  loading,
  onSubmit,
  editingProduct,
  form,
  excelSheetUploading,
  triggerImportClick,
}) => {
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const downloadTemplate = async () => {
    const XLSX = await import("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products Template");

    const max_len = [22, 12, 12, 8, 15, 8, 14, 25, 12, 16, 12, 12, 14];
    worksheet["!cols"] = max_len.map((w) => ({ wch: w }));

    XLSX.writeFile(workbook, "product_import_template.xlsx");
    message.success("Import template downloaded.");
  };

  const { data: categories = [], refetch: refetchCats } = useCategoriesQuery({
    enabled: open,
  });

  const { data: brands = [], refetch: refetchBrands } = useBrandsQuery({
    enabled: open,
  });

  const { data: activeStoresResponse } = useActiveStoresQuery(undefined, {
    enabled: open,
  });
  const stores = Array.isArray(activeStoresResponse)
    ? activeStoresResponse
    : ((activeStoresResponse as any)?.stores ?? []);

  const { data: suppliers = [], refetch: refetchSuppliers } = useSuppliersQuery(
    {
      enabled: open,
    },
  );

  const { data: printers = [] } = usePrintersQuery(undefined, {
    enabled: open,
  });

  const { data: unitOptions = [] } = useActiveUnitsQuery({
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      if (editingProduct) {
        let imgs: string[] = [];
        try {
          imgs = JSON.parse(editingProduct.image_paths || "[]");
        } catch {
          imgs = [];
        }
        const initialFiles = imgs.map((img, idx) => ({
          uid: `-${idx}`,
          name: img,
          status: "done",
          url: `${IMAGE_URL}/${img}`,
          response: img,
        }));
        setFileList(initialFiles);
      } else {
        setFileList([]);
        const currentStoreIds = form.getFieldValue("store_ids");
        if (
          stores?.length > 0 &&
          (!currentStoreIds || currentStoreIds?.length === 0)
        ) {
          const mainStore = stores.find((s) => s.main_store === 1);
          if (mainStore) {
            form.setFieldsValue({
              store_ids: [mainStore.id],
            });
          }
        }
      }
    }
  }, [open, editingProduct, stores, form]);

  const handleUploadChange = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();

      const formData = new FormData();
      if (editingProduct) {
        formData.append("id", String(editingProduct.id));
      }

      formData.append("title", values.title);
      formData.append("category_id", String(values.category_id));
      formData.append("brand_id", String(values.brand_id));
      formData.append("unit", values.unit);
      formData.append("sku_code", values.sku_code);
      formData.append("mrp", String(values.mrp));
      formData.append("alert_quantity", String(values.alert_quantity || 10));
      formData.append("description", values.description || "");
      formData.append("barcode", values.barcode || "");
      formData.append(
        "is_negative_qty_allowed",
        String(values.is_negative_qty_allowed ? 1 : 0),
      );
      formData.append("printer_id", values.printer_id || "");

      if (!editingProduct) {
        const selectedStores = values.store_ids || [];
        formData.append("store_ids", JSON.stringify(selectedStores));
      } else {
        formData.append("store_id", String(values.store_id));
      }

      const selectedSuppliers = values.suppliers || [];
      formData.append("suppliers", JSON.stringify(selectedSuppliers));

      const stockBatches = (values.stocks || []).map((s: any) => ({
        ...s,
        expiry_date: s.expiry_date && typeof s.expiry_date.format === "function"
          ? s.expiry_date.format("YYYY-MM-DD")
          : s.expiry_date || null,
      }));
      formData.append("stocks", JSON.stringify(stockBatches));

      const existingImagesKept = fileList
        .filter((file) => file.status === "done" && file.url)
        .map((file) => file.name);
      formData.append("existing_images", JSON.stringify(existingImagesKept));

      const newFiles = fileList.filter(
        (file) => !file.url && file.originFileObj,
      );
      newFiles.forEach((file) => {
        formData.append("images[]", file.originFileObj);
      });

      (form as any)._formData = formData;
      onSubmit();
    } catch {
      message.error("Please fill all required fields correctly.");
    }
  };

  return (
    <Drawer
      title={drawerTitle(
        editingProduct ? "Edit Product Details" : "Register New Product",
      )}
      extra={
        <Space>
          <Button size="middle" onClick={downloadTemplate}>
            Template
          </Button>
          <Button
            size="middle"
            loading={excelSheetUploading}
            onClick={triggerImportClick}
          >
            Import
          </Button>
        </Space>
      }
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: onClose },
          {
            label: editingProduct ? "Save Product" : "Create Product",
            type: "primary",
            loading,
            onClick: handleFormSubmit,
          },
        ],
      })}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          {/* Title */}
          <Form.Item
            label="Product Title"
            name="title"
            rules={[{ required: true, message: "Product title is required." }]}
          >
            <Input placeholder="Enter product title (e.g. Fresh Milk 1L)" />
          </Form.Item>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item
              label={
                <div className="flex items-center gap-x-1 justify-between w-full">
                  <span>Category</span>
                  <Button
                    type="link"
                    size="small"
                    className="p-0 h-auto"
                    onClick={() => setCatModalOpen(true)}
                  >
                    (Add New)
                  </Button>
                </div>
              }
              name="category_id"
              rules={[{ required: true, message: "Please select a category." }]}
            >
              <Select
                placeholder="Select category"
                showSearch
                optionFilterProp="children"
              >
                {categories?.map((cat: any) => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={
                <div className="flex items-center justify-between gap-x-1  w-full">
                  <span>Brand</span>
                  <Button
                    type="link"
                    size="small"
                    className="p-0 h-auto"
                    onClick={() => setBrandModalOpen(true)}
                  >
                    (Add New)
                  </Button>
                </div>
              }
              name="brand_id"
              rules={[{ required: true, message: "Please select a brand." }]}
            >
              <Select
                placeholder="Select brand"
                showSearch
                optionFilterProp="children"
              >
                {brands?.map((br: any) => (
                  <Option key={br.id} value={br.id}>
                    {br.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {!editingProduct ? (
            <Form.Item
              label="Assigned Stores"
              name="store_ids"
              rules={[
                {
                  required: true,
                  message: "Please select at least one store.",
                },
              ]}
              extra="This product will be created separately inside each selected store."
            >
              <Select
                mode="multiple"
                placeholder="Select stores for this product"
                style={{ width: "100%" }}
                optionFilterProp="children"
              >
                {stores?.map((store) => (
                  <Option key={store.id} value={store.id}>
                    {store.name} {store.main_store === 1 ? "(Main Store)" : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <Form.Item
              label="Store"
              name="store_id"
              extra="A product's store assignment cannot be changed after registration."
            >
              <Select disabled style={{ width: "100%" }}>
                {stores.map((store) => (
                  <Option key={store.id} value={store.id}>
                    {store.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Unit & SKU Code */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item
              label="Unit"
              name="unit"
              rules={[
                { required: true, message: "Unit selection is required." },
              ]}
            >
              <Select placeholder="Select unit">
                {unitOptions?.map((unit) => (
                  <Option key={unit?.id} value={unit?.name}>
                    {unit?.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="SKU Code" name="sku_code">
              <Input
                placeholder="Enter unique SKU code"
                disabled={!!editingProduct}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item label="MRP (Max Retail Price - Optional)" name="mrp">
              <InputNumber
                style={{ width: "100%" }}
                formatter={(value) =>
                  `Rs. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  (value ? value.replace(/Rs\.\s?|(,*)/g, "") : "") as any
                }
                placeholder="0.00"
                min={0}
              />
            </Form.Item>

            <Form.Item label="Alert Quantity" name="alert_quantity">
              <InputNumber style={{ width: "100%" }} min={0} placeholder="10" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item
              label="Barcode"
              name="barcode"
              extra={
                editingProduct ? undefined : "Auto-generated if left blank"
              }
            >
              <Input
                placeholder="Optional Custom Barcode"
                disabled={!!editingProduct}
              />
            </Form.Item>

            <Form.Item
              label={
                <div className="flex items-center gap-x-1 justify-between w-full">
                  <span>Suppliers</span>
                  <Button
                    type="link"
                    size="small"
                    className="p-0 h-auto"
                    onClick={() => setSupplierModalOpen(true)}
                  >
                    (Add New)
                  </Button>
                </div>
              }
              name="suppliers"
            >
              <Select
                mode="multiple"
                placeholder="Select suppliers"
                style={{ width: "100%" }}
                optionFilterProp="children"
              >
                {suppliers?.map((supp: any) => (
                  <Option key={supp.id} value={supp.id}>
                    {supp.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Form.Item
              name="is_negative_qty_allowed"
              valuePropName="checked"
              style={{ marginBottom: "16px" }}
            >
              <Checkbox>Allow Negative Quantity?</Checkbox>
            </Form.Item>

            <Form.Item
              label="Printer Reference"
              name="printer_id"
              style={{ marginBottom: "16px" }}
            >
              <Select placeholder="Select Printer (Optional)" allowClear>
                {printers?.map((printer) => (
                  <Option key={printer.id} value={printer.id}>
                    {printer.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item label="Description (Optional)" name="description">
            <Input.TextArea
              placeholder="Enter product descriptions, features, or details"
              rows={2}
              style={{ resize: "none" }}
            />
          </Form.Item>

          <Form.Item label="Product Images">
            <Upload
              listType="picture"
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={() => false}
              multiple
            >
              <Button icon={<UploadOutlined />}>Select Product Images</Button>
            </Upload>
          </Form.Item>

          <Divider orientation="left">
            <span className="text-sm font-semibold text-blue-600">
              Stock Batches / Price Management
            </span>
          </Divider>

          <StockForm form={form} />
        </Form>
      </Spin>

      <CategoryModal
        open={catModalOpen}
        onClose={() => {
          setCatModalOpen(false);
          refetchCats();
        }}
      />
      <BrandModal
        open={brandModalOpen}
        onClose={() => {
          setBrandModalOpen(false);
          refetchBrands();
        }}
      />
      <SupplierModal
        open={supplierModalOpen}
        onClose={() => {
          setSupplierModalOpen(false);
          refetchSuppliers();
        }}
      />
    </Drawer>
  );
};
